import { useAuth0 } from '@auth0/auth0-react'
import {
  Box,
  Container,
  Divider,
  GridItem,
  SimpleGrid,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import {
  Campus,
  Disclaimer_Swnz_GlobalSettingsCollectionFragment,
  Intake,
  InventoryCourse,
  Maybe,
  StudyInNzDetailBanner_Swnz_GlobalSettingsCollectionFragment,
} from 'content-service'
import dynamic from 'next/dynamic'
import { Fragment, useRef, useState } from 'react'
import {
  FinderResultItemEnquiryButton,
  FinderResultItemSaveButton,
} from 'src/components/Search/FinderResultItemButtons'
import {
  createUtagLinkClickEvent,
  createUtagStartEnquiryEvent,
  INVENTORY_LABELS,
  TEALIUM_LINK_CATEGORY,
} from 'tracking'
import {
  CtaButton,
  FinderDetailBanner,
  FORM_COMPONENTS,
  GridLayout,
  H2,
  JumpAheadMenu,
  RichTextRenderer,
  SocialLinks,
  StudyOptionItemType,
  useHandleModalCloseResetData,
} from 'ui'
import { HtmlStringParser } from 'ui/src/components/HtmlStringParser'
import { useEnquiryModalOnSignIn, useSavedModalOnSignIn } from 'ui/src/forms/helpers/enquiryForm'
import { createCtaButton } from 'utils/src/helpers'
import {
  DetailPageHeader,
  DetailPageHeaderContainer,
  DetailPageHeaderDetail,
} from '../../components/DetailPageHeader'
import SavedSuccessModal, { SavedModalStatusType } from '../../components/SavedSuccessModal'
import SignUpModal, { LoginConnectProps, SignUpType } from '../../components/SignUpModal'
import { EnquiryFormConnectProps } from '../../connectors'
import useUpdateInventoryItem from '../../hooks/useUpdateInventoryItem'
import { CourseOptionDetail, CourseOptionsModal } from './CourseOptionsModal'
import {
  determineShowHideDetailBanner,
  findActiveIntakes,
  findFistValidIntakeCampus,
  formatDeliverModeMap,
  formatStartDate,
  validateCourseDetailProps,
  validateDetailBannerProps,
} from './helpers'

interface CourseDetailPageProps {
  courseDetail: InventoryCourse
  disclaimer: Disclaimer_Swnz_GlobalSettingsCollectionFragment | undefined | null
  detailBanner: StudyInNzDetailBanner_Swnz_GlobalSettingsCollectionFragment | undefined | null
  pageUrl: string
}

const FormModal = dynamic(() => import('ui/src/forms/FormModal'))
const EnquiryFormConnect = dynamic(() => import('../../connectors/EnquiryForm'))

const CourseDetailPage = ({
  courseDetail,
  disclaimer,
  detailBanner,
  pageUrl,
}: CourseDetailPageProps) => {
  const isEnquired = useRef<boolean>(false)

  const campuses = courseDetail?.provider?.campuses?.filter((campus) => campus?.active)
  const intakes = findActiveIntakes(courseDetail?.intakes, campuses)
  const activeIntakeCampusIds = intakes?.map((intake) => intake?.campus)
  const campusesWithIntakes = campuses
    ?.map((course) => activeIntakeCampusIds?.includes(course?.id))
    .filter(Boolean)
  const firstValidIntake = intakes?.[0] || undefined
  const firstValidIntakeCampus: Campus | null =
    findFistValidIntakeCampus(firstValidIntake?.campus, campuses) || null
  const duration = firstValidIntake?.duration ?? undefined
  const campus = firstValidIntakeCampus?.name ? `${firstValidIntakeCampus?.name}` : ''
  const nextStartDate = formatStartDate(firstValidIntake?.startDate)
  const validCourseDetailProps = validateCourseDetailProps(courseDetail)
  const validDetailBannerProps = validateDetailBannerProps(detailBanner)
  const disclaimerRichText = disclaimer?.items[0]?.richText || null
  const displayJumpAheadMenu =
    validCourseDetailProps.detailsHtmlString && validCourseDetailProps.summaryHtmlString
  const showDetailBanner = determineShowHideDetailBanner(firstValidIntake, intakes)

  /**
   * 1. Hooks and Component state relevant to <CourseEnquiryForm/InstitutionEnquiryForm/> modal.
   */
  const {
    isOpen: isEnquiryFormModalOpen,
    onOpen: onEnquiryFormModalOpen,
    onClose: onEnquiryFormModalClose,
  } = useDisclosure()
  const {
    isOpen: isCourseOptionsModalOpen,
    onOpen: onCourseOptionsModalOpen,
    onClose: onCourseOptionsModalClose,
  } = useDisclosure()

  const {
    isOpen: isSignUpOpen,
    onOpen: onSignUpModalOpen,
    onClose: onSignUpModalClose,
  } = useDisclosure()

  const {
    isOpen: isSaveModalOpen,
    onClose: onSaveModalClose,
    onOpen: onSaveModalOpen,
  } = useDisclosure()

  const { isAuthenticated, isLoading } = useAuth0()

  const handleModalCloseResetData = useHandleModalCloseResetData()

  const [currentLoginState, setCurrentLoginState] = useState<LoginConnectProps>({
    openModal: false,
  })

  const [actionData, setActionData] = useState<EnquiryFormConnectProps | null>(null)
  const [savedStatus, setSavedStatus] = useState<SavedModalStatusType>(SavedModalStatusType.Default)

  useSavedModalOnSignIn(onSignUpModalOpen, setSavedStatus)
  useEnquiryModalOnSignIn(onEnquiryFormModalOpen, setActionData)

  const handleEnquiryFormModalOpen = () => {
    createUtagStartEnquiryEvent({
      pageName: INVENTORY_LABELS.COURSE.PAGE_NAME,
      inventoryId: validCourseDetailProps.id,
      inventoryObjectType: INVENTORY_LABELS.COURSE.INVENTORY_OBJECT_TYPE,
      levelOfStudy: validCourseDetailProps.levelOfStudy.tag,
      areaOfStudyArray: validCourseDetailProps.areasOfStudy,
      subjectOfStudyArray: validCourseDetailProps.subjectsOfStudy,
      deliveryModeArray: validCourseDetailProps.deliveryModes,
      courseName: validCourseDetailProps.name,
      providerName: validCourseDetailProps.providerName,
      providerType: validCourseDetailProps.providerType,
      providerSubtype: validCourseDetailProps.providerSubtypeList,
      campusRegionArray: validCourseDetailProps.campusRegions,
      linkLabel: 'Enquire',
    })
    if (!isLoading) {
      setActionData({
        formId: courseDetail.id as string,
        formComponent: FORM_COMPONENTS.COURSE_ENQUIRY,
        courseName: courseDetail.name as string,
        providerName: courseDetail?.provider?.name as string,
        deliveryMode: firstValidIntake?.deliveryMode?.tag as string,
        onClose: () => false,
      })

      if (isAuthenticated) {
        onEnquiryFormModalOpen()
      } else {
        onSignUpModalOpen()
        setCurrentLoginState({
          loginType: SignUpType.Enquiry,
          openModal: true,
          inventory: {
            formId: courseDetail.id as string,
            formComponent: FORM_COMPONENTS.COURSE_ENQUIRY,
            courseName: courseDetail.name as string,
            providerName: courseDetail?.provider?.name as string,
            deliveryMode: firstValidIntake?.deliveryMode?.tag as string,
            onClose: () => false,
          },
        })
      }
    }
  }

  const handleSaveItem = useUpdateInventoryItem(onSaveModalOpen)

  const handleSaveClick = async (
    props: EnquiryFormConnectProps & { itemType: StudyOptionItemType }
  ) => {
    if (!isLoading) {
      setActionData(props)
      if (isAuthenticated) {
        handleSaveItem(props.formId, StudyOptionItemType.Course)
          .then(() => {
            setSavedStatus(SavedModalStatusType.Success)
          })
          .catch((err) => {
            setSavedStatus(SavedModalStatusType.Error)
          })
      } else {
        onSignUpModalOpen()
        setCurrentLoginState({
          loginType: SignUpType.Save,
          openModal: true,
          inventory: props,
        })
      }
    }
  }

  const handleCourseOptionsModalOpen = () => {
    createUtagLinkClickEvent({
      pageName: validCourseDetailProps.name,
      linkLabel: `Show more course options`,
      linkCategory: TEALIUM_LINK_CATEGORY.CTA,
    })
    onCourseOptionsModalOpen()
  }

  return (
    <Fragment>
      <Box dir="ltr">
        <DetailPageHeaderContainer>
          <DetailPageHeaderContainer.Header>
            <DetailPageHeader
              linkHeading={validCourseDetailProps.providerSubtype}
              provider={validCourseDetailProps.providerName}
              heading={validCourseDetailProps.name}
              linkHref={validCourseDetailProps.linkHref}
              logoUrl={validCourseDetailProps.logoUrl}
            />
          </DetailPageHeaderContainer.Header>
          <DetailPageHeaderContainer.Details>
            <DetailPageHeaderDetail
              detailHeading="This course is available"
              detail={validCourseDetailProps.modeOfStudyOptions}
              isLeadDetail
            />
            <DetailPageHeaderDetail
              detailHeading="Level of Study"
              detail={validCourseDetailProps.levelOfStudy.name}
            />
            {duration && (
              <DetailPageHeaderDetail detailHeading="Duration" detail={duration} isDuration />
            )}
            {nextStartDate && (
              <DetailPageHeaderDetail
                detailHeading="Next start date"
                detail={`Expected ${nextStartDate}`}
              />
            )}
            {campusesWithIntakes?.length == 1 && (
              <DetailPageHeaderDetail detailHeading="Campus" detail={campus} />
            )}
          </DetailPageHeaderContainer.Details>
          <Box data-testid="multiple-intakes">
            {intakes && intakes?.length > 1 && (
              <>
                <CtaButton
                  variant="link"
                  textDecoration="underline"
                  fontFamily="enz400"
                  mt={{ base: 4, lg: 6 }}
                  onClick={handleCourseOptionsModalOpen}
                >
                  {`Show more course options`}
                </CtaButton>
                <CourseOptionsModal
                  isOpen={isCourseOptionsModalOpen}
                  onClose={onCourseOptionsModalClose}
                  courseName={validCourseDetailProps.name}
                  courseLevel={validCourseDetailProps.levelOfStudy.name}
                >
                  {intakes.map((intake: Maybe<Intake>) => {
                    if (!intake) {
                      return null
                    }

                    const campusName = campuses?.find(
                      (element) => element?.id === intake.campus
                    )?.name

                    return (
                      <Fragment key={intake.id}>
                        <Divider my={5} color="deepblue.200" opacity={1} />
                        <SimpleGrid columns={{ lg: 4 }} spacing={{ base: 4, lg: 6 }}>
                          <GridItem>
                            <CourseOptionDetail
                              detailHeading="Study mode"
                              detail={
                                intake?.deliveryMode?.tag
                                  ? formatDeliverModeMap[intake?.deliveryMode?.tag]
                                  : '-'
                              }
                            />
                          </GridItem>
                          <GridItem>
                            <CourseOptionDetail
                              detailHeading="Duration"
                              detail={intake?.duration ?? '-'}
                            />
                          </GridItem>
                          <GridItem>
                            <CourseOptionDetail
                              detailHeading="Start date"
                              detail={intake.startDate ? formatStartDate(intake.startDate) : '-'}
                            />
                          </GridItem>
                          <GridItem>
                            <CourseOptionDetail
                              detailHeading="Campus"
                              detail={campusName ? campusName : '-'}
                            />
                          </GridItem>
                        </SimpleGrid>
                      </Fragment>
                    )
                  })}
                </CourseOptionsModal>
              </>
            )}
          </Box>
          <DetailPageHeaderContainer.CtaButtons>
            <FinderResultItemEnquiryButton
              colorScheme="deepblue"
              openEnquiryForm={handleEnquiryFormModalOpen}
              inventoryId={validCourseDetailProps.id}
              height="60px"
              isEnquired={isEnquired.current}
            />
            <FinderResultItemSaveButton
              colorScheme="deepblue"
              inventoryId={validCourseDetailProps.id}
              saveInventoryItem={() =>
                handleSaveClick({
                  providerName: validCourseDetailProps.name,
                  formId: validCourseDetailProps.id,
                  formComponent: '',
                  loginType: SignUpType.Save,
                  onClose: () => false,
                  itemType: StudyOptionItemType.Course,
                })
              }
              height={'60px'}
            />
          </DetailPageHeaderContainer.CtaButtons>
        </DetailPageHeaderContainer>

        <Container>
          <GridLayout>
            <GridItem colSpan={{ base: 4, md: 5, lg: 7 }}>
              {displayJumpAheadMenu && (
                <Box marginBottom={14} display={{ base: 'none', md: 'block' }}>
                  <JumpAheadMenu />
                </Box>
              )}
              {validCourseDetailProps.detailsHtmlString && (
                <Box marginBottom={14} borderTopColor="deepblue.200">
                  <>
                    <H2 data-type="jump-ahead" data-name="Summary" data-title="Summary">
                      Summary
                    </H2>
                    <HtmlStringParser htmlString={validCourseDetailProps.detailsHtmlString} />
                  </>
                </Box>
              )}
              {validCourseDetailProps.entryRequirements && (
                <Box marginBottom={14}>
                  <>
                    <H2
                      data-type="jump-ahead"
                      data-name="Entry Requirements"
                      data-title="Entry Requirements"
                    >
                      Entry criteria
                    </H2>
                    <HtmlStringParser htmlString={validCourseDetailProps.entryRequirements} />
                  </>
                </Box>
              )}
            </GridItem>
            <GridItem
              colSpan={{ base: 4, md: 3, lg: 4 }}
              colStart={{ base: 1, md: 6, lg: 9 }}
              gridRow={1}
            >
              {showDetailBanner && (
                <FinderDetailBanner title="Studying in NZ" type="info">
                  <RichTextRenderer richText={validDetailBannerProps.detailBannerRichText} />
                  <FinderDetailBanner.CtaButtonContainer>
                    {validDetailBannerProps.detailBannerCtaButtonItems.map((item, index) => {
                      const ctaButton = createCtaButton(item)
                      return (
                        <FinderDetailBanner.CtaButton
                          key={index}
                          type={index === 0 ? 'primary' : 'secondary'}
                          href={ctaButton?.href ?? ''}
                          label={ctaButton?.label ?? ''}
                          openInNewWindow={Boolean(ctaButton?.openInNewWindow)}
                          isExternalLink={Boolean(ctaButton?.isExternalLink)}
                          modalId={ctaButton?.modalId}
                        />
                      )
                    })}
                  </FinderDetailBanner.CtaButtonContainer>
                </FinderDetailBanner>
              )}
              {displayJumpAheadMenu && (
                <Box marginTop={12} marginBottom={10} display={{ base: 'block', md: 'none' }}>
                  <JumpAheadMenu />
                </Box>
              )}
              <Box display={{ base: 'none', md: 'block' }}>
                <SocialLinks
                  title="Share this course"
                  pageUrl={pageUrl}
                  pageInfo={courseDetail?.name ?? ''}
                  socialItems={['facebook', 'twitter', 'email']}
                />
              </Box>
            </GridItem>
          </GridLayout>
        </Container>
        <Container display={{ base: 'block', md: 'none' }} marginBottom={16}>
          <SocialLinks
            title="Share this course"
            pageUrl={pageUrl}
            pageInfo={courseDetail?.name ?? ''}
            socialItems={['facebook', 'twitter', 'email']}
          />
        </Container>
        <Container>
          <Box
            marginBottom={14}
            paddingTop={10}
            borderTop="1px solid"
            borderTopColor="deepblue.200"
          >
            <Text fontFamily="enz500">Disclaimer</Text>
            <RichTextRenderer richText={disclaimerRichText} />
          </Box>
        </Container>
      </Box>

      {isEnquiryFormModalOpen && actionData && (
        <FormModal
          isOpen={isEnquiryFormModalOpen}
          onClose={() => handleModalCloseResetData(onEnquiryFormModalClose)}
        >
          <EnquiryFormConnect
            {...actionData}
            onClose={() => handleModalCloseResetData(onEnquiryFormModalClose)}
            onSuccess={() => {
              isEnquired.current = true
            }}
          />
        </FormModal>
      )}
      {!isAuthenticated && (
        <SignUpModal
          loginState={currentLoginState}
          isOpen={isSignUpOpen}
          onClose={onSignUpModalClose}
        />
      )}
      <SavedSuccessModal
        loginState={currentLoginState}
        isOpen={isSaveModalOpen}
        onClose={() => handleModalCloseResetData(onSaveModalClose)}
        itemType="providers"
        status={savedStatus}
      />
    </Fragment>
  )
}

export default CourseDetailPage
